import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@api";

import "../Wizard/CreatePageWizard.css";
import FullPagePreview from "../Components/FullPagePreview.jsx";
import Step1Template from "../Shared/Steps/Step1Template.jsx";
import templateRegistry from "../Registry/index.js";
import createFieldSetter from "./createFieldSetter.js";

function EditOrganizationPage() {
  const navigate = useNavigate();
  const { orgId, pageId } = useParams();

  // ---------------------------------------------------------------------------
  // Template-driven content and media state
  // ---------------------------------------------------------------------------

  const [templateState, setTemplateState] = useState({});
  const [imageState, setImageState] = useState({});
  const [galleryState, setGalleryState] = useState({});

  // ---------------------------------------------------------------------------
  // Page identity and publishing state
  // ---------------------------------------------------------------------------

  const [pageName, setPageName] = useState("");
  const [pagePath, setPagePath] = useState("");
  const [pathTouched, setPathTouched] = useState(false);
  const [templateId, setTemplateId] = useState("default-homepage");
  const [isPublished, setIsPublished] = useState(true);
  const [isHomepage, setIsHomepage] = useState(false);
  const [pageType, setPageType] = useState("page");

  // ---------------------------------------------------------------------------
  // Organization and theme state
  // ---------------------------------------------------------------------------

  const [organizationData, setOrganizationData] = useState({});
  const [themeMode, setThemeMode] = useState("dark");
  const [primaryColor, setPrimaryColor] = useState("#111111");
  const [accentColor, setAccentColor] = useState("#d4af37");
  const [templateAccess, setTemplateAccess] = useState([]);
  const [orgSlug, setOrgSlug] = useState("");

  // ---------------------------------------------------------------------------
  // Editor UI state
  // ---------------------------------------------------------------------------

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [step, setStep] = useState(2);
  const [showFullPreview, setShowFullPreview] = useState(false);
  const [previewScaledHeight, setPreviewScaledHeight] = useState(0);

  const previewInnerRef = useRef(null);

  // The registry determines which editor and Step 2 component are active.
  const editorConfig =
    templateRegistry[templateId]?.editorConfig || null;

  const Step2Component =
    templateRegistry[templateId]?.step2Component || null;

  // ---------------------------------------------------------------------------
  // Load the page, organization settings, and config-driven media state
  // ---------------------------------------------------------------------------

  useEffect(() => {
    let alive = true;

    const loadPage = async () => {
      try {
        const [pageResponse, organizationResponse] = await Promise.all([
          api.get(`/api/organization-page/${orgId}`),
          api.get(`/api/organizations/${orgId}`),
        ]);

        if (!alive) return;

        const organization =
          organizationResponse.data?.organization || {};

        const foundPage = (pageResponse.data?.pages || []).find(
          (page) => String(page.id) === String(pageId)
        );

        if (!foundPage) {
          alert("Page not found.");
          navigate(`/dashboard/organizations/${orgId}`);
          return;
        }

        const content =
          foundPage.content ||
          foundPage.pageSettings?.content ||
          foundPage.settings?.content ||
          {};

        const foundEditorConfig =
          templateRegistry[foundPage.template_id]?.editorConfig || null;

        // Load all template text/content fields through the template config.
        if (foundEditorConfig) {
          const loadedTemplateState = foundEditorConfig.loadContent
            ? foundEditorConfig.loadContent(content)
            : {
              ...(foundEditorConfig.initialContent || {}),
              ...content,
            };

          setTemplateState(loadedTemplateState);
        }

        // Build one generic state entry for every configured single-image slot.
        const initialImageState = Object.fromEntries(
          Object.entries(foundEditorConfig?.imageFields || {}).map(
            ([fieldKey, fieldConfig]) => [
              fieldKey,
              {
                file: null,
                existingUrl: content[fieldConfig.contentKey] || "",
                remove: false,
              },
            ]
          )
        );

        // Build one generic state entry for every configured gallery slot.
        const initialGalleryState = Object.fromEntries(
          Object.entries(foundEditorConfig?.galleryFields || {}).map(
            ([fieldKey, fieldConfig]) => [
              fieldKey,
              {
                files: [],
                existingUrls: Array.isArray(content[fieldConfig.contentKey])
                  ? content[fieldConfig.contentKey]
                  : [],
                remove: false,
              },
            ]
          )
        );

        const organizationSettings = organization.settings || {};

        setOrganizationData(organization);
        setImageState(initialImageState);
        setGalleryState(initialGalleryState);

        setTemplateAccess(organization.templateAccess || []);
        setOrgSlug(organization.slug || "");

        setThemeMode(
          String(organizationSettings.theme_mode || "dark")
            .trim()
            .toLowerCase()
        );
        setPrimaryColor(
          organizationSettings.primary_color || "#111111"
        );
        setAccentColor(
          organizationSettings.accent_color || "#d4af37"
        );

        setPageName(foundPage.name || "");
        setPagePath(foundPage.path || "");
        setPathTouched(true);
        setTemplateId(
          foundPage.template_id || "default-homepage"
        );
        setIsPublished(Boolean(foundPage.is_published));
        setIsHomepage(
          Boolean(foundPage.is_homepage) ||
          foundPage.type === "home"
        );
        setPageType(foundPage.type || "page");
      } catch (error) {
        console.error("Failed to load page", error);
        alert("Could not load page.");
      } finally {
        if (alive) {
          setLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      alive = false;
    };
  }, [orgId, pageId, navigate]);

  // ---------------------------------------------------------------------------
  // Resolve current preview URLs for every configured media slot
  // ---------------------------------------------------------------------------

  const resolvedImageUrls = useMemo(() => {
    return Object.fromEntries(
      Object.entries(imageState).map(
        ([fieldKey, fieldState]) => {
          let resolvedUrl = "";

          if (fieldState?.file) {
            resolvedUrl = URL.createObjectURL(fieldState.file);
          } else if (!fieldState?.remove) {
            resolvedUrl = fieldState?.existingUrl || "";
          }

          return [fieldKey, resolvedUrl];
        }
      )
    );
  }, [imageState]);

  const resolvedGalleryImageUrls = useMemo(() => {
    return Object.fromEntries(
      Object.entries(galleryState).map(
        ([fieldKey, fieldState]) => {
          const existingUrls = Array.isArray(
            fieldState?.existingUrls
          )
            ? fieldState.existingUrls
            : [];

          const newPreviewUrls = Array.isArray(fieldState?.files)
            ? fieldState.files.map((file) =>
              URL.createObjectURL(file)
            )
            : [];

          const resolvedUrls = fieldState?.remove
            ? newPreviewUrls
            : [...existingUrls, ...newPreviewUrls];

          return [fieldKey, resolvedUrls];
        }
      )
    );
  }, [galleryState]);

  // ---------------------------------------------------------------------------
  // Build registry-driven preview data
  // ---------------------------------------------------------------------------

  const previewData = useMemo(() => {
    const activeEditorConfig =
      templateRegistry[templateId]?.editorConfig || null;

    if (!activeEditorConfig?.buildContent) {
      return undefined;
    }

    const content = activeEditorConfig.buildContent(
      templateState,
      {
        resolvedImageUrls,
        resolvedGalleryImageUrls,
      }
    );

    return {
      organization: {
        ...organizationData,
        name:
          organizationData?.name || "Your Organization",
        slug:
          organizationData?.slug ||
          pagePath?.trim() ||
          "your-page",
      },
      page: {
        template_id: templateId,
        content,
      },
      settings: {
        theme_mode: themeMode,
        primary_color: primaryColor,
        accent_color: accentColor,
        logo_url:
          organizationData?.settings?.logo_url || "",
      },
    };
  }, [
    templateState,
    templateId,
    pagePath,
    organizationData,
    themeMode,
    primaryColor,
    accentColor,
    resolvedImageUrls,
    resolvedGalleryImageUrls,
  ]);

  // ---------------------------------------------------------------------------
  // Generic template field helper
  // ---------------------------------------------------------------------------

  const setTemplateField = (field, valueOrUpdater) => {
    createFieldSetter(
      setTemplateState,
      field
    )(valueOrUpdater);
  };

  // ---------------------------------------------------------------------------
  // Generic single-image helpers
  // ---------------------------------------------------------------------------

  const getImageField = (fieldKey) => {
    return (
      imageState[fieldKey] || {
        file: null,
        existingUrl: "",
        remove: false,
      }
    );
  };

  const setImageFile = (fieldKey, file) => {
    setImageState((previousState) => {
      const currentField = previousState[fieldKey] || {
        file: null,
        existingUrl: "",
        remove: false,
      };

      return {
        ...previousState,
        [fieldKey]: {
          ...currentField,
          file,
          remove: file ? false : currentField.remove,
        },
      };
    });
  };

  const setImageRemoved = (fieldKey, remove) => {
    setImageState((previousState) => {
      const currentField = previousState[fieldKey] || {
        file: null,
        existingUrl: "",
        remove: false,
      };

      return {
        ...previousState,
        [fieldKey]: {
          ...currentField,
          file: remove ? null : currentField.file,
          remove,
        },
      };
    });
  };

  // ---------------------------------------------------------------------------
  // Generic gallery helpers
  // ---------------------------------------------------------------------------

  const getGalleryField = (fieldKey) => {
    return (
      galleryState[fieldKey] || {
        files: [],
        existingUrls: [],
        remove: false,
      }
    );
  };

  const setGalleryFiles = (fieldKey, files) => {
    setGalleryState((previousState) => {
      const currentField = previousState[fieldKey] || {
        files: [],
        existingUrls: [],
        remove: false,
      };

      const nextFiles = Array.isArray(files) ? files : [];

      return {
        ...previousState,
        [fieldKey]: {
          ...currentField,
          files: nextFiles,
          remove:
            nextFiles.length > 0
              ? false
              : currentField.remove,
        },
      };
    });
  };

  const setGalleryExistingUrls = (
    fieldKey,
    valueOrUpdater
  ) => {
    setGalleryState((previousState) => {
      const currentField = previousState[fieldKey] || {
        files: [],
        existingUrls: [],
        remove: false,
      };

      const nextExistingUrls =
        typeof valueOrUpdater === "function"
          ? valueOrUpdater(currentField.existingUrls)
          : valueOrUpdater;

      return {
        ...previousState,
        [fieldKey]: {
          ...currentField,
          existingUrls: Array.isArray(nextExistingUrls)
            ? nextExistingUrls
            : [],
        },
      };
    });
  };

  const setGalleryRemoved = (fieldKey, remove) => {
    setGalleryState((previousState) => {
      const currentField = previousState[fieldKey] || {
        files: [],
        existingUrls: [],
        remove: false,
      };

      return {
        ...previousState,
        [fieldKey]: {
          ...currentField,
          files: remove ? [] : currentField.files,
          remove,
        },
      };
    });
  };

  // Each template config maps the generic helpers to the prop names expected
  // by that template's own Step 2 editor component.
  const step2Props = editorConfig?.getStep2Props
    ? editorConfig.getStep2Props({
      templateState,
      setTemplateField,
      getImageField,
      setImageFile,
      setImageRemoved,
      getGalleryField,
      setGalleryFiles,
      setGalleryExistingUrls,
      setGalleryRemoved,
    })
    : {};

  // ---------------------------------------------------------------------------
  // Save all template content and media through the active editor config
  // ---------------------------------------------------------------------------

  const handleSave = async () => {
    setSaving(true);

    try {
      const content = editorConfig?.buildContent
        ? editorConfig.buildContent(templateState, {
          resolvedImageUrls,
          resolvedGalleryImageUrls,
        })
        : null;

      const formData = new FormData();

      const configuredImageFields =
        editorConfig?.imageFields || {};

      const configuredGalleryFields =
        editorConfig?.galleryFields || {};

      const imageContentKeys = Object.values(
        configuredImageFields
      )
        .map((fieldConfig) => fieldConfig?.contentKey)
        .filter(Boolean);

      const galleryContentKeys = Object.values(
        configuredGalleryFields
      )
        .map((fieldConfig) => fieldConfig?.contentKey)
        .filter(Boolean);

      const removedImageKeys = Object.entries(
        configuredImageFields
      )
        .filter(
          ([fieldKey]) =>
            imageState[fieldKey]?.remove === true
        )
        .map(([, fieldConfig]) => fieldConfig.contentKey);

      const removedGalleryKeys = Object.entries(
        configuredGalleryFields
      )
        .filter(
          ([fieldKey]) =>
            galleryState[fieldKey]?.remove === true
        )
        .map(([, fieldConfig]) => fieldConfig.contentKey);

      // Append each configured single-image upload.
      Object.entries(configuredImageFields).forEach(
        ([fieldKey, fieldConfig]) => {
          const file = imageState[fieldKey]?.file || null;
          const contentKey = fieldConfig?.contentKey;

          if (!file || !contentKey) return;

          formData.append(contentKey, file);
        }
      );

      // Append every new file for each configured gallery.
      Object.entries(configuredGalleryFields).forEach(
        ([fieldKey, fieldConfig]) => {
          const files = Array.isArray(
            galleryState[fieldKey]?.files
          )
            ? galleryState[fieldKey].files
            : [];

          const contentKey = fieldConfig?.contentKey;

          if (files.length === 0 || !contentKey) return;

          files.forEach((file) => {
            formData.append(contentKey, file);
          });
        }
      );

      formData.append("template_id", templateId);
      formData.append(
        "is_homepage",
        String(isHomepage)
      );
      formData.append(
        "is_published",
        String(isPublished)
      );
      formData.append(
        "type",
        isHomepage ? "home" : "page"
      );
      formData.append(
        "content",
        JSON.stringify(content)
      );
      formData.append(
        "imageContentKeys",
        JSON.stringify(imageContentKeys)
      );
      formData.append(
        "removedImageKeys",
        JSON.stringify(removedImageKeys)
      );
      formData.append(
        "galleryContentKeys",
        JSON.stringify(galleryContentKeys)
      );
      formData.append(
        "removedGalleryKeys",
        JSON.stringify(removedGalleryKeys)
      );

      if (isHomepage) {
        formData.append("path", "");
      } else {
        formData.append("name", pageName.trim());
        formData.append(
          "path",
          pagePath.trim().replace(/^\/+/, "")
        );
      }

      await api.put(
        `/api/organization-page/${orgId}/${pageId}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      sessionStorage.setItem(
        "dashboard_success_message",
        "Page updated successfully."
      );

      navigate(`/dashboard/organizations/${orgId}`);
    } catch (error) {
      console.error("Failed to save page", error);
      alert("Could not save page.");
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Keep the scaled inline preview container matched to its content height
  // ---------------------------------------------------------------------------

  useLayoutEffect(() => {
    const previewElement = previewInnerRef.current;

    if (!previewElement) return;

    const previewFrame = previewElement.closest(
      ".cpw-preview-frame"
    );

    const frameStyles = previewFrame
      ? getComputedStyle(previewFrame)
      : null;

    const previewScale = frameStyles
      ? Number(
        frameStyles.getPropertyValue("--preview-scale")
      ) || 0.44
      : 0.44;

    const updatePreviewHeight = () => {
      setPreviewScaledHeight(
        previewElement.scrollHeight * previewScale
      );
    };

    updatePreviewHeight();

    const resizeObserver = new ResizeObserver(
      updatePreviewHeight
    );

    resizeObserver.observe(previewElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, [previewData]);

  if (loading) {
    return null;
  }

  return (
    <div className="cpw-page">
      <div className="cpw-shell">
        <div className="cpw-top">
          <div className="cpw-title">
            <h1>Edit Page</h1>
            <p>Step {step} of 2</p>
          </div>

          <div className="cpw-progress">
            {[1, 2].map((stepNumber) => (
              <button
                key={stepNumber}
                type="button"
                className={`dot ${step >= stepNumber ? "on" : ""
                  }`}
                onClick={() => setStep(stepNumber)}
                aria-label={`Go to step ${stepNumber}`}
              />
            ))}
          </div>
        </div>

        <div className="cpw-grid">
          <div className="cpw-preview">
            <div className="cpw-preview-toolbar">
              <button
                type="button"
                className="cpw-preview-open"
                onClick={() => setShowFullPreview(true)}
              >
                Full preview
              </button>
            </div>

            <div
              className={`cpw-preview-frame ${themeMode}`}
            >
              <div className="cpw-preview-scroll">
                <div
                  className="cpw-preview-content"
                  style={{
                    height: previewScaledHeight
                      ? `${previewScaledHeight}px`
                      : "auto",
                  }}
                >
                  <div
                    className="cpw-preview-scale"
                    ref={previewInnerRef}
                  >
                    <FullPagePreview
                      pageData={previewData}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="preview-note">
              This is a preview of your page.
            </div>
          </div>

          <div className="cpw-form">
            {step !== 1 && (
              <div style={{ marginBottom: "12px" }}>
                <button
                  type="button"
                  className="ghost"
                  onClick={() => setStep(1)}
                >
                  Change Template
                </button>
              </div>
            )}

            {step === 1 && (
              <Step1Template
                templateId={templateId}
                setTemplateId={setTemplateId}
                name={pageName}
                setName={setPageName}
                path={pagePath}
                setPath={setPagePath}
                pathTouched={pathTouched}
                setPathTouched={setPathTouched}
                isHomepage={isHomepage}
                pageType={pageType}
                templateAccess={templateAccess}
                orgSlug={orgSlug}
              />
            )}

            {step === 2 && Step2Component && (
              <Step2Component {...step2Props} />
            )}

            <div className="cpw-nav">
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  navigate(
                    `/dashboard/organizations/${orgId}`
                  );
                }}
              >
                Back
              </button>

              {step < 2 ? (
                <button
                  type="button"
                  className="next"
                  onClick={() => setStep(2)}
                >
                  Next
                </button>
              ) : (
                <button
                  type="button"
                  className="next"
                  onClick={handleSave}
                  disabled={
                    saving ||
                    (!isHomepage && !pageName.trim())
                  }
                >
                  {saving ? "Saving..." : "Save"}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showFullPreview && (
        <div
          className="cpw-modal-backdrop"
          onClick={() => setShowFullPreview(false)}
        >
          <div
            className="cpw-modal"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="cpw-modal-close"
              onClick={() => setShowFullPreview(false)}
              aria-label="Close"
            >
              ✕
            </button>

            <div className="cpw-modal-preview">
              <FullPagePreview pageData={previewData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EditOrganizationPage;